import type { Theme } from './types/theme';
import type { ThemeCategory } from './data/themeCategories';

export const LOCALES = ['zh', 'en'] as const;
export type Locale = typeof LOCALES[number];

const LOCALE_STORAGE_KEY = 'vibeui-locale';

export function isLocale(value: string | null | undefined): value is Locale {
  return value === 'zh' || value === 'en';
}

export function detectBrowserLocale(): Locale {
  const language = typeof navigator === 'undefined' ? '' : navigator.language;
  return language.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

export function getInitialLocale(): Locale {
  if (typeof window === 'undefined') {
    return 'en';
  }

  const savedLocale = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  return isLocale(savedLocale) ? savedLocale : detectBrowserLocale();
}

export function persistLocale(locale: Locale) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }
}

export const appCopy = {
  zh: {
    brandName: 'VibeUI',
    title: 'VibeUI',
    subtitle: '快速浏览、预览和筛选好看的 UI 风格，找到适合你产品的视觉气质。',
    libraryTitle: '风格库',
    libraryDescription: '搜索、筛选并打开完整页面预览。当前保留单风格快速切换，减少干扰。',
    searchLabel: '搜索风格',
    searchPlaceholder: '搜索风格、品牌、类别或关键词...',
    showing: (visible: number, total: number) => `显示 ${visible} / ${total} 个风格`,
    allCategory: '全部',
    previewFullPage: '预览',
    exportDesignMd: '导出 DESIGN.md',
    exportPrompt: '复制 Prompt',
    noMatchesTitle: '没有匹配的风格',
    noMatchesBody: '换一个关键词，或重置分类筛选。',
    resetFilters: '重置筛选',
    languageLabel: '切换语言',
    languageToggle: 'English',
    addToShortlist: '加入候选',
    removeFromShortlist: '移出候选',
    shortlisted: '已候选',
    shortlistToggle: (count: number) => `候选 ${count}`,
    shortlistTitle: '候选集',
    shortlistDescription: '把看中的风格先收进来，最后集中复看和导出。',
    shortlistEmptyTitle: '还没有候选风格',
    shortlistEmptyBody: '在风格卡片上点击“加入候选”，先收几个有感觉的方向。',
    shortlistViewAll: '查看候选',
    shortlistClose: '关闭候选集',
    shortlistClear: '清空候选',
    copiedPrompt: 'Prompt 已复制',
    promptCopyFailed: '复制失败，请重试',
    valueWorkbench: {
      entry: '增值功能',
      syncing: '正在同步云端内容...',
      syncFailed: '同步失败，请稍后重试',
    },
    auth: {
      actionFailed: '账户操作失败，请检查 Google 登录配置。',
      closeDialog: '关闭登录面板',
      configureHint: '需要配置 Supabase URL、公开 key，并在 Supabase 开启 Google provider。',
      dialogDescription: '注册和登录使用同一个入口。先用 Google 账户继续，后续再扩展邮箱或团队登录。',
      dialogTitle: '登录或注册',
      entry: '登录 / 注册',
      googleUnavailable: 'Google 登录脚本加载失败，请检查网络后重试。',
      loading: '检查登录',
      signInWithGoogle: '使用 Google 继续',
      signedInAs: (name: string) => `已登录：${name}`,
      signOut: '退出',
    },
    pageTypes: {
      landing: '落地页',
      dashboard: '仪表盘',
    },
    preview: {
      close: '关闭预览',
      source: '来源：DESIGN.md',
      themeNavigation: '风格切换',
      previousTheme: '上一个风格',
      nextTheme: '下一个风格',
      switcher: '风格列表',
      toggleSwitcher: '打开风格列表',
      closeSwitcher: '关闭风格列表',
      searchThemes: '搜索风格',
      searchPlaceholder: '搜索风格...',
      current: '当前',
      keyboardHint: '可用方向键快速切换风格。',
    },
    footer: {
      sourcePrefix: '风格来源',
      note: 'VibeUI 用固定内容与布局比较不同视觉 token。',
    },
  },
  en: {
    brandName: 'VibeUI',
    title: 'VibeUI',
    subtitle: 'Browse, preview, and choose beautiful UI styles before committing to a product direction.',
    libraryTitle: 'Style Library',
    libraryDescription: 'Search, filter, and open a full-page preview. Single-style switching stays fast and focused.',
    searchLabel: 'Search styles',
    searchPlaceholder: 'Search styles, brands, categories, or keywords...',
    showing: (visible: number, total: number) => `Showing ${visible} of ${total} styles`,
    allCategory: 'All',
    previewFullPage: 'Preview',
    exportDesignMd: 'Export DESIGN.md',
    exportPrompt: 'Copy prompt',
    noMatchesTitle: 'No styles match those filters',
    noMatchesBody: 'Try another keyword or reset the category filter.',
    resetFilters: 'Reset filters',
    languageLabel: 'Switch language',
    languageToggle: '中文',
    addToShortlist: 'Add to shortlist',
    removeFromShortlist: 'Remove',
    shortlisted: 'Shortlisted',
    shortlistToggle: (count: number) => `Shortlist ${count}`,
    shortlistTitle: 'Shortlist',
    shortlistDescription: 'Save promising styles, then revisit and export the best fit.',
    shortlistEmptyTitle: 'No styles shortlisted yet',
    shortlistEmptyBody: 'Add styles from the gallery when a direction feels promising.',
    shortlistViewAll: 'View shortlist',
    shortlistClose: 'Close shortlist',
    shortlistClear: 'Clear shortlist',
    copiedPrompt: 'Prompt copied',
    promptCopyFailed: 'Copy failed, try again',
    valueWorkbench: {
      entry: 'Value features',
      syncing: 'Syncing cloud content...',
      syncFailed: 'Sync failed, try again later',
    },
    auth: {
      actionFailed: 'Account action failed. Check the Google sign-in setup.',
      closeDialog: 'Close sign-in panel',
      configureHint: 'Configure Supabase URL, public key, and the Google provider in Supabase.',
      dialogDescription: 'Sign-in and registration use the same entry. Continue with Google for now; email or team login can come later.',
      dialogTitle: 'Sign in or register',
      entry: 'Sign in / Register',
      googleUnavailable: 'Google sign-in could not load. Check the network and try again.',
      loading: 'Checking session',
      signInWithGoogle: 'Continue with Google',
      signedInAs: (name: string) => `Signed in as ${name}`,
      signOut: 'Sign out',
    },
    pageTypes: {
      landing: 'Landing',
      dashboard: 'Dashboard',
    },
    preview: {
      close: 'Close preview',
      source: 'Source: DESIGN.md',
      themeNavigation: 'Theme navigation',
      previousTheme: 'Previous theme',
      nextTheme: 'Next theme',
      switcher: 'Theme switcher',
      toggleSwitcher: 'Toggle theme switcher',
      closeSwitcher: 'Close theme switcher',
      searchThemes: 'Search themes',
      searchPlaceholder: 'Search themes...',
      current: 'Current',
      keyboardHint: 'Use arrow keys to move between styles.',
    },
    footer: {
      sourcePrefix: 'Style source',
      note: 'VibeUI compares visual tokens with fixed content and layout.',
    },
  },
} as const;

const categoryLabels: Record<Locale, Record<ThemeCategory, string>> = {
  zh: {
    'AI & LLM': 'AI 与大模型',
    'Developer Tools': '开发者工具',
    'Backend & DevOps': '后端与运维',
    Productivity: '效率工具',
    'Design & Creative': '设计与创意',
    'Finance & Commerce': '金融与商业',
    'Consumer & Media': '消费与媒体',
    Automotive: '汽车',
    'Enterprise & Cloud': '企业与云',
  },
  en: {
    'AI & LLM': 'AI & LLM',
    'Developer Tools': 'Developer Tools',
    'Backend & DevOps': 'Backend & DevOps',
    Productivity: 'Productivity',
    'Design & Creative': 'Design & Creative',
    'Finance & Commerce': 'Finance & Commerce',
    'Consumer & Media': 'Consumer & Media',
    Automotive: 'Automotive',
    'Enterprise & Cloud': 'Enterprise & Cloud',
  },
};

const densityLabels: Record<Locale, Record<Theme['mood']['density'], string>> = {
  zh: {
    low: '低密度',
    medium: '中密度',
    high: '高密度',
  },
  en: {
    low: 'Low density',
    medium: 'Medium density',
    high: 'High density',
  },
};

export function getCategoryLabel(category: ThemeCategory, locale: Locale): string {
  return categoryLabels[locale][category];
}

export function getDensityLabel(density: Theme['mood']['density'], locale: Locale): string {
  return densityLabels[locale][density];
}

export function getThemeCardSummary(theme: Theme, locale: Locale): string {
  if (locale === 'en') {
    return theme.description;
  }

  return '在统一页面结构中预览这套视觉 token 的色彩、密度与组件语气。';
}
